// Import react elements
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';

// Import context function
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";
import { AccountLastUpdatedContext } from "global/context/AccountLastUpdatedContext";

// Import API calls
import { getDashboardData } from "api/APIStrichCalls";

// Import hooks
import usePageTitle from "global/hooks/usePageTitle";

// Import components
import { Grid, GridItem } from "global/components/Grid";
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import SubscriptionSummaryPanel from "pages/account/components/SubscriptionSummaryPanel";

// Import utilities
import { FormatCurrency } from "global/utilities/ConvertCurrency";

// Import css stylesheet
import "./DashboardPage.css";




// Fetch dashboard summary from API
export const loadDashboardData = async (token, setDashboardData, setLoading, setErrorMessage) => {

    // Set loading state 
    setLoading(true);

    // Clear previous errors
    setErrorMessage("");

    // Call API
    const response = await getDashboardData(token);

    // Handle successful response
    if (response?.success && response?.results) {

        setDashboardData(response.results);

    } else {

        // Handle API error response
        setErrorMessage(response?.error_message || response?.error || "Unable to load dashboard data.");

    }

    // End loading state
    setLoading(false);

};





const Dashboard = () => {

    // Set title inside the page
    usePageTitle("Dashboard");

    const navigate = useNavigate();

    // Get the token from AuthContext
    const { token, authReady } = useContext(AuthContext);

    // Get the account data from UserContext
    const { userData, subscriptionLevels, subscriptionPackages } = useContext(UserContext);

    // Get the global account update markers
    const { lastUpdated } = useContext(AccountLastUpdatedContext);

    // Get the dashboard-related markers
    const ordersLastUpdated = lastUpdated?.orders_last_updated || null;
    const transactionsLastUpdated = lastUpdated?.transactions_last_updated || null;

    // Store the latest account marker values without forcing the initial load effect to depend on them
    const ordersLastUpdatedRef = useRef(null);
    const transactionsLastUpdatedRef = useRef(null);

    // Store the marker values that were current when the dashboard was last loaded
    const loadedOrdersLastUpdatedRef = useRef(null);
    const loadedTransactionsLastUpdatedRef = useRef(null);

    // Prevent overlapping automatic refreshes
    const autoRefreshInProgressRef = useRef(false);

    // Use the account currency when available
    const accountCurrency = userData?.currency || "GBP";

    // Store dashboard data
    const [dashboardData, setDashboardData] = useState(null);

    // Track loading state
    const [loading, setLoading] = useState(true);

    // Track loading errors
    const [errorMessage, setErrorMessage] = useState("");



    // Format revenue values that are already returned in the account currency
    const formatAccountCurrency = (amount) => {

        // Return the amount formatted using the account currency
        return FormatCurrency(accountCurrency, amount);

    };



    // Keep the latest dashboard marker values in refs for the initial load effect
    useEffect(() => {

        // Store latest order marker
        ordersLastUpdatedRef.current = ordersLastUpdated;

        // Store latest transaction marker
        transactionsLastUpdatedRef.current = transactionsLastUpdated;

    }, [ordersLastUpdated, transactionsLastUpdated]);



    // Load dashboard data when auth becomes available
    useEffect(() => {

        // Return early if auth is not ready or token is missing
        if (!authReady || !token) {

            return;

        }

        // Trigger data load
        loadDashboardData(token, setDashboardData, setLoading, setErrorMessage).then(() => {

            // Store the marker values from the initial dashboard load
            loadedOrdersLastUpdatedRef.current = ordersLastUpdatedRef.current;
            loadedTransactionsLastUpdatedRef.current = transactionsLastUpdatedRef.current;

        });

    }, [token, authReady]);

    // Refresh dashboard data when relevant global markers change
    useEffect(() => {

        // Only refresh when auth is ready
        if (!authReady || !token) { return; }

        // Ignore empty/default order markers
        const ordersMarkerChanged = ordersLastUpdated && ordersLastUpdated !== "0001-01-01 00:00:00" && loadedOrdersLastUpdatedRef.current && ordersLastUpdated !== loadedOrdersLastUpdatedRef.current;

        // Ignore empty/default transaction markers
        const transactionsMarkerChanged = transactionsLastUpdated && transactionsLastUpdated !== "0001-01-01 00:00:00" && loadedTransactionsLastUpdatedRef.current && transactionsLastUpdated !== loadedTransactionsLastUpdatedRef.current;

        // Stop if nothing relevant has changed
        if (!ordersMarkerChanged && !transactionsMarkerChanged) { return; }

        // Stop if an automatic refresh is already running
        if (autoRefreshInProgressRef.current) { return; }

        // Mark the automatic refresh as running
        autoRefreshInProgressRef.current = true;

        // Reload dashboard data quietly in the background
        loadDashboardData(token, setDashboardData, () => {}, () => {}).then(() => {

            // Store the marker values from the refreshed dashboard load
            loadedOrdersLastUpdatedRef.current = ordersLastUpdated;
            loadedTransactionsLastUpdatedRef.current = transactionsLastUpdated;

        }).finally(() => {

            // Mark the automatic refresh as finished
            autoRefreshInProgressRef.current = false;

        });

    }, [authReady, token, ordersLastUpdated, transactionsLastUpdated]);


    // Extract dashboard values with safe defaults where appropriate

    // Number of open orders awaiting action
    const openOrders = dashboardData?.open_orders ?? 0;

    // Revenue for last 30 days (already in pounds)
    const revenue30Days = dashboardData?.revenue_30_days ?? 0;

    // Revenue for previous 30 days (already in pounds)
    const revenuePrevious30Days = dashboardData?.revenue_previous_30_days ?? 0;

    // Revenue change value (keep default as 0)
    const revenueChangeValue = dashboardData?.revenue_change_value ?? 0;

    // Revenue change percent (do NOT default - used to control display)
    const revenueChangePercent = dashboardData?.revenue_change_percent;

    // Total POD designs
    const podDesignsTotal = dashboardData?.pod_designs_total ?? 0;

    // Total unsettled account balance (returned in pence)
    const balance = dashboardData?.balance ?? 0;

    // Available account balance (returned in pence)
    const availableBalance = dashboardData?.available_balance ?? 0;



    // Determine revenue change direction for styling
    const revenueChangeDirection = revenueChangeValue > 0 ? "positive" : revenueChangeValue < 0 ? "negative" : "neutral";

    // Determine whether to show revenue change (only if percent exists)
    const showRevenueChange = revenueChangePercent !== null && revenueChangePercent !== undefined;

    // Determine prefix symbol for change values
    const revenueChangePrefix = revenueChangeValue > 0 ? "+" : "";

    // Format the absolute revenue change with the correct prefix
    const formattedRevenueChangeValue = `${revenueChangePrefix}${formatAccountCurrency(Math.abs(revenueChangeValue))}`;



    // Render loading state
    if (loading) {

        return (

            <Grid>

                <GridItem>

                    <div className="dashboard-status-panel">

                        <LoadingSpinner />
                        <p>Loading dashboard...</p>

                    </div>

                </GridItem>

            </Grid>

        );

    }



    // Render error state
    if (errorMessage) {

        return (

            <Grid>

                <GridItem>

                    <div className="dashboard-status-panel dashboard-status-panel--error">

                        <h3>Dashboard unavailable</h3>
                        <p>{errorMessage}</p>

                    </div>

                </GridItem>

            </Grid>

        );

    }



    // Render dashboard content
    return (

        <Grid>

            {/* Subscription Card */}
            <GridItem className="dashboard-subscription-card cursor-pointer" onClick={() => navigate("/account")}>

                <SubscriptionSummaryPanel
                    userData={userData}
                    subscriptionLevels={subscriptionLevels}
                    subscriptionPackages={subscriptionPackages}
                    buttonTitle="Upgrade"
                    buttonSize="m"
                    showDescription={false}
                    smallRenewDisplay={true}
                />

            </GridItem>



            {/* Open Orders Card */}
            <GridItem className="dashboard-stat-card cursor-pointer" onClick={() => navigate("/orders")}>

                <p className="dashboard-stat-card__label">Orders</p>
                <p className="dashboard-stat-card__value">{openOrders.toLocaleString("en-GB")}</p>
                <p className="dashboard-stat-card__meta">Orders currently awaiting action</p>

            </GridItem>



            {/* Order Value Card */}
            <GridItem className="dashboard-stat-card">

                <p className="dashboard-stat-card__label">Order Value (30 days)</p>
                <p className="dashboard-stat-card__value">{formatAccountCurrency(revenue30Days)}</p>

                {/* Only show change if percentage exists */}
                <p className={`dashboard-stat-card__change`}>
                    {formattedRevenueChangeValue}
                    {showRevenueChange && (
                        <span className={`dashboard-stat-card__change--${revenueChangeDirection}`}> ({revenueChangePrefix}{Math.round(Number(revenueChangePercent))}%)</span>
                    )}
                </p>

                <p className="dashboard-stat-card__meta">
                    Previous 30 days order value: {formatAccountCurrency(revenuePrevious30Days)}
                </p>

            </GridItem>


            {/* Balance Card */}
            <GridItem className="dashboard-stat-card cursor-pointer" onClick={() => navigate("/transactions")}>

                <p className="dashboard-stat-card__label">Balance</p>
                <p className="dashboard-stat-card__value">{FormatCurrency(accountCurrency, balance, true)}</p>
                <p className="dashboard-stat-card__meta">Available: {FormatCurrency(accountCurrency, availableBalance, true)}</p>

            </GridItem>


            {/* POD Designs Card */}
            <GridItem className="dashboard-stat-card cursor-pointer" onClick={() => navigate("/designs")}>

                <p className="dashboard-stat-card__label">Designs</p>
                <p className="dashboard-stat-card__value">{podDesignsTotal.toLocaleString("en-GB")}</p>
                <p className="dashboard-stat-card__meta">Total POD designs in your account</p>

            </GridItem>



        </Grid>

    );

};

export default Dashboard;